"use client";

import { Box, Flex, Icon } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { FiExternalLink } from "react-icons/fi";
import { HiOutlineChevronRight } from "react-icons/hi2";
import type { GetLocationResponse } from "@/openapi/generated";
import { EntryType, MediaGroup } from "@/openapi/generated/models";
import {
  useClassRoomsGetPublic,
  usePublicLocationsNavLink,
  usePublicPageReturnContext,
  useTenantData,
} from "@/hooks";
import { useMediaAssetGet, usePresignedImageErrorRecovery } from "@/features/media/hooks";
import {
  buildGoogleMapsEmbedUrl,
  buildGoogleMapsOpenUrl,
} from "@/lib/utils/googleMaps";
import {
  formatLocationAddressSingleLine,
  getLocationMapQuery,
} from "@/lib/utils/formatLocationAddress";
import { getLocationOptionalDescription } from "@/lib/utils/locationPublic";
import { resolveLocationCoverUrl } from "@/lib/utils/media/fetch-media-asset";
import { useThemeTokens } from "@/lib/theme/tokens";
import { ImageWithFallback } from "@/components/shared";
import { Button, Link, NavLink, Text } from "@/components/ui";
import { ClassShareButton } from "@/views/classes/components";
import { PublicDetailBreadcrumbs } from "@/views/shared/public-listing";
import { ClassroomPublicModal } from "@/views/classrooms";
import { LocationClassesSection } from "./LocationClassesSection";
import { LocationMapEmbed } from "./LocationMapEmbed";
import { PageTypeGlobal } from "@/lib/types";

export interface LocationPublicPageViewProps {
  location: GetLocationResponse;
  locationId: number;
}

interface ClassroomRowProps {
  name: string;
  onClick: () => void;
  isLast: boolean;
}

const ClassroomRow = ({ name, onClick, isLast }: ClassroomRowProps) => {
  const theme = useThemeTokens();

  return (
    <Box
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      bg="transparent"
      border="none"
      p={0}
      cursor="pointer"
      onClick={onClick}
    >
      <Flex
        align="center"
        justify="space-between"
        gap={4}
        py={4}
        borderBottomWidth={isLast ? 0 : "1px"}
        borderColor={theme.borderGlass.subtle}
        sx={{
          "@media (hover: hover)": {
            "&:hover [data-classroom-name]": { color: theme.accent.primary },
            "&:hover [data-classroom-chevron]": {
              color: theme.accent.primary,
              transform: "translateX(2px)",
            },
          },
        }}
      >
        <Box minW={0}>
          <Text fontSize="xs" color={theme.text.tertiary} mb={1}>
            Classroom
          </Text>
          <Text
            data-classroom-name
            fontSize="sm"
            fontWeight="600"
            color={theme.text.primary}
            transition="color 0.2s ease"
            noOfLines={2}
          >
            {name}
          </Text>
        </Box>
        <Icon
          as={HiOutlineChevronRight}
          data-classroom-chevron
          boxSize="16px"
          color={theme.text.tertiary}
          flexShrink={0}
          transition="transform 0.2s ease, color 0.2s ease"
          aria-hidden
        />
      </Flex>
    </Box>
  );
};

export const LocationPublicPageView = ({
  location,
  locationId,
}: LocationPublicPageViewProps) => {
  const theme = useThemeTokens();
  const { tenantId } = useTenantData();
  const { fromListing, backLink } = usePublicPageReturnContext({
    page: "location",
  });
  const locationsNav = usePublicLocationsNavLink();
  const isSingleLocationStudio = locationsNav?.label === "Location";
  const [previewClassRoomId, setPreviewClassRoomId] = useState<number | null>(null);

  const { url: coverMediaUrl, refetch: refetchCover } = useMediaAssetGet(
    {
      tenantId,
      ownerType: EntryType.Location,
      ownerId: locationId,
      mediaGroup: MediaGroup.Cover,
    },
    { enabled: locationId > 0 && !!tenantId, requireAuth: false },
  );

  const coverImageUrl = resolveLocationCoverUrl(coverMediaUrl);
  const handleCoverImageError = usePresignedImageErrorRecovery(
    coverImageUrl,
    refetchCover,
  );

  const {
    data: classRoomsPage,
    isPending: isClassRoomsPending,
    isError: isClassRoomsError,
    isFetching: isClassRoomsFetching,
    refetch: refetchClassRooms,
  } = useClassRoomsGetPublic({
    page: 1,
    pageSize: 1000,
    active: true,
    enabled: locationId > 0,
  });

  const addressSingleLine = useMemo(
    () => formatLocationAddressSingleLine(location),
    [location],
  );
  const mapQuery = useMemo(() => getLocationMapQuery(location), [location]);
  const mapEmbedUrl = mapQuery ? buildGoogleMapsEmbedUrl(mapQuery) : null;
  const mapOpenUrl = mapQuery ? buildGoogleMapsOpenUrl(mapQuery) : null;

  const locationDescriptionText = useMemo(
    () => getLocationOptionalDescription(location),
    [location],
  );

  const metaParts = useMemo(() => {
    const parts: string[] = [];
    if (location.phoneNumber?.trim()) parts.push(location.phoneNumber.trim());
    if (location.email?.trim()) parts.push(location.email.trim());
    if (location.timeZone?.trim()) parts.push(location.timeZone.trim());
    return parts;
  }, [location.email, location.phoneNumber, location.timeZone]);

  const classroomsAtLocation = useMemo(() => {
    const items = classRoomsPage?.items ?? [];
    const id = Number(locationId);
    return items
      .filter(
        (room) =>
          room.isActive !== false &&
          room.location != null &&
          Number(room.location.locationId) === id,
      )
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
  }, [classRoomsPage, locationId]);

  const locationName = location.name?.trim() || "Location";

  return (
    <Flex direction="column" maxW="720px" mx="auto" w="100%">
      <Flex justify="space-between" align="center" gap={3} mb={{ base: 5, md: 6 }}>
        <PublicDetailBreadcrumbs
          parentHref={
            fromListing === "locations"
              ? backLink.href
              : PageTypeGlobal.Locations
          }
          parentLabel="Locations"
          currentLabel={locationName}
        />
        <ClassShareButton title={locationName} subject="location" iconOnlyOnMobile={false} />
      </Flex>

      <Box
        mb={{ base: 5, md: 6 }}
        borderRadius="12px"
        overflow="hidden"
        borderWidth="1px"
        borderColor={theme.borderGlass.default}
        w="100%"
        position="relative"
      >
        {!coverImageUrl ? (
          <Box
            position="absolute"
            inset={0}
            zIndex={0}
            bg={`linear-gradient(135deg, ${theme.accent.primary} 0%, ${theme.accent.mutedHex || theme.accent.muted} 100%)`}
          />
        ) : null}
        <Box position="relative" zIndex={1}>
          <ImageWithFallback
            src={coverImageUrl}
            alt={locationName}
            fallbackType="image"
            width="100%"
            height={{ base: "200px", md: "280px" }}
            borderRadius="12px"
            objectFit="cover"
            priority
            onImageError={handleCoverImageError}
          />
        </Box>
      </Box>

      <Box borderBottomWidth="1px" borderColor={theme.borderGlass.subtle} pb={{ base: 5, md: 6 }}>
        <Text
          fontSize="xs"
          fontWeight="600"
          letterSpacing="0.14em"
          textTransform="uppercase"
          color={theme.text.tertiary}
          mb={2}
        >
          Location
        </Text>
        <Text
          as="h1"
          fontSize={{ base: "2xl", md: "3xl" }}
          fontWeight="600"
          letterSpacing="-0.03em"
          color={theme.text.primary}
          lineHeight="1.15"
        >
          {locationName}
        </Text>

        {addressSingleLine ? (
          <Text mt={3} fontSize="sm" color={theme.text.secondary} lineHeight="1.6">
            {mapOpenUrl ? (
              <Link
                href={mapOpenUrl}
                target="_blank"
                rel="noopener noreferrer"
                color={theme.text.secondary}
                fontWeight="500"
                sx={{
                  "@media (hover: hover)": {
                    "&:hover": { color: theme.accent.primary, textDecoration: "underline" },
                  },
                }}
              >
                {addressSingleLine}
              </Link>
            ) : (
              addressSingleLine
            )}
          </Text>
        ) : null}

        {metaParts.length > 0 ? (
          <Flex direction="column" gap={1} mt={4}>
            {location.phoneNumber?.trim() ? (
              <Link
                href={`tel:${location.phoneNumber.replace(/\s/g, "")}`}
                fontSize="sm"
                fontWeight="500"
                color={theme.accent.primary}
                sx={{
                  "@media (hover: hover)": {
                    "&:hover": { textDecoration: "underline" },
                  },
                }}
              >
                {location.phoneNumber.trim()}
              </Link>
            ) : null}
            {location.email?.trim() ? (
              <Link
                href={`mailto:${location.email.trim()}`}
                fontSize="sm"
                fontWeight="500"
                color={theme.accent.primary}
                wordBreak="break-word"
                sx={{
                  "@media (hover: hover)": {
                    "&:hover": { textDecoration: "underline" },
                  },
                }}
              >
                {location.email.trim()}
              </Link>
            ) : null}
            {location.timeZone?.trim() ? (
              <Text fontSize="sm" color={theme.text.tertiary}>
                {location.timeZone.trim()}
              </Text>
            ) : null}
          </Flex>
        ) : null}

        {mapOpenUrl ? (
          <Box mt={5}>
            <Button
              as="a"
              href={mapOpenUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              size="sm"
              leftIcon={<FiExternalLink />}
            >
              Directions
            </Button>
          </Box>
        ) : null}
      </Box>

      <Box py={{ base: 5, md: 6 }} borderBottomWidth="1px" borderColor={theme.borderGlass.subtle}>
        {locationDescriptionText ? (
          <Text fontSize="md" color={theme.text.secondary} lineHeight="1.75" whiteSpace="pre-line">
            {locationDescriptionText}
          </Text>
        ) : (
          <Text fontSize="md" color={theme.text.tertiary} lineHeight="1.75" fontStyle="italic">
            No description has been added for this location yet.
          </Text>
        )}
      </Box>

      {mapEmbedUrl ? (
        <Box py={{ base: 5, md: 6 }} borderBottomWidth="1px" borderColor={theme.borderGlass.subtle}>
          <Text
            fontSize="xs"
            fontWeight="600"
            letterSpacing="0.14em"
            textTransform="uppercase"
            color={theme.text.tertiary}
            mb={4}
          >
            Map
          </Text>
          <LocationMapEmbed embedUrl={mapEmbedUrl} title={`Map for ${locationName}`} />
          {mapOpenUrl ? (
            <Button
              as="a"
              href={mapOpenUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              size="sm"
              leftIcon={<FiExternalLink />}
              mt={4}
            >
              Open in Google Maps
            </Button>
          ) : null}
        </Box>
      ) : null}

      <LocationClassesSection
        locationName={locationName}
        isSingleLocationStudio={isSingleLocationStudio}
      />

      {isClassRoomsPending && !classRoomsPage ? (
        <Box py={{ base: 5, md: 6 }}>
          <Text variant="secondary" fontSize="sm">
            Loading classrooms…
          </Text>
        </Box>
      ) : isClassRoomsError ? (
        <Box py={{ base: 5, md: 6 }}>
          <Flex direction="column" gap={3} align="flex-start">
            <Text variant="secondary" fontSize="sm">
              We couldn&apos;t load classrooms for this location.
            </Text>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetchClassRooms()}
              isLoading={isClassRoomsFetching}
              isDisabled={isClassRoomsFetching}
            >
              Try again
            </Button>
          </Flex>
        </Box>
      ) : classroomsAtLocation.length > 0 ? (
        <Box pt={{ base: 2, md: 3 }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            letterSpacing="0.14em"
            textTransform="uppercase"
            color={theme.text.tertiary}
            mb={2}
          >
            Classrooms · {classroomsAtLocation.length}
          </Text>
          <Box borderTopWidth="1px" borderColor={theme.borderGlass.subtle}>
            {classroomsAtLocation.map((room, index) => (
              <ClassroomRow
                key={String(room.classRoomId)}
                name={room.name || "Classroom"}
                isLast={index === classroomsAtLocation.length - 1}
                onClick={() => setPreviewClassRoomId(Number(room.classRoomId))}
              />
            ))}
          </Box>
        </Box>
      ) : null}

      <ClassroomPublicModal
        isOpen={previewClassRoomId != null}
        onClose={() => setPreviewClassRoomId(null)}
        classRoomId={previewClassRoomId}
      />
    </Flex>
  );
};
